/**
 * FinancialSection - Financial Tab Content
 *
 * Extracts the financial configuration from ScenarioEditor Tab 4.
 * Contains two sub-tabs:
 * - Budget: CapEx and OpEx tables
 * - Commercial: Commercial assumptions configuration
 */

import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { AttachMoney, TrendingUp } from '@mui/icons-material';
import BudgetTab from '../../../components/BudgetTab';
import { OpExTable, vortxColors } from '@vortx/shared-ui';
import CommercialAssumptionsTab from '../../../components/CommercialAssumptionsTab';

/**
 * FinancialSection Component
 *
 * Main component for Tab 4 (Financial) content with sub-tabs.
 *
 * @param {Object} props
 * @param {Object} props.systemSizes - System sizing data
 * @param {Object} props.budget - Budget hook state
 * @param {Object} props.scenarioForm - Scenario form hook state (for hasSystemConfigured and generators)
 * @param {Object} props.commercialAssumptions - Commercial assumptions hook state
 * @param {Object} props.selectedEquipment - Selected equipment data and setters
 * @param {number} props.subTab - Current sub-tab index (external control)
 * @param {Function} props.setSubTab - Setter for sub-tab index (external control)
 */
function FinancialSection({
    systemSizes,
    budget,
    scenarioForm,
    commercialAssumptions,
    selectedEquipment,
    subTab = 0,
    setSubTab,
}) {
    // Use internal state if no external state provided
    const [internalSubTab, setInternalSubTab] = useState(subTab);
    const financialSubTab = setSubTab ? subTab : internalSubTab;
    const setFinancialSubTab = setSubTab || setInternalSubTab;

    const hasSystemConfigured = scenarioForm.usePv || scenarioForm.useStorage || scenarioForm.useGenerator;

    return (
        <>
            <Tabs
                value={financialSubTab}
                onChange={(e, newValue) => setFinancialSubTab(newValue)}
                sx={{
                    mb: 3,
                    '& .MuiTab-root': { fontWeight: 500 },
                    '& .Mui-selected': { color: vortxColors.gold },
                    '& .MuiTabs-indicator': { backgroundColor: vortxColors.gold },
                }}
            >
                <Tab icon={<AttachMoney sx={{ fontSize: 18 }} />} iconPosition="start" label="Budget" />
                <Tab icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" label="Commercial" />
            </Tabs>

            {/* Budget Sub-tab */}
            {financialSubTab === 0 && (
                <>
                    <BudgetTab
                        systemSizes={systemSizes}
                        budget={budget}
                        hasSystemConfigured={hasSystemConfigured}
                        generators={scenarioForm.generators}
                        selectedSolarData={selectedEquipment.selectedSolarData}
                        setSelectedSolarData={selectedEquipment.setSelectedSolarData}
                        selectedBatteryData={selectedEquipment.selectedBatteryData}
                        setSelectedBatteryData={selectedEquipment.setSelectedBatteryData}
                        selectedGeneratorData={selectedEquipment.selectedGeneratorData}
                        setSelectedGeneratorData={selectedEquipment.setSelectedGeneratorData}
                        selectedSwitchgearData={selectedEquipment.selectedSwitchgearData}
                        setSelectedSwitchgearData={selectedEquipment.setSelectedSwitchgearData}
                    />
                    {/* OpEx Table below CapEx */}
                    <Box sx={{ mt: 3 }}>
                        <OpExTable
                            systemSizes={systemSizes}
                            budget={budget}
                            hasSystemConfigured={hasSystemConfigured}
                        />
                    </Box>
                </>
            )}

            {/* Commercial Sub-tab */}
            {financialSubTab === 1 && (
                <CommercialAssumptionsTab
                    commercialAssumptions={commercialAssumptions}
                    readOnly={false}
                />
            )}
        </>
    );
}

export default FinancialSection;
